import { ReactComponent as AddIcon } from "assets/images/icons/btn_add.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

import React, { useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import { SolidButton } from "components/buttons/SolidButton";

export const parseUserAccountCsv = (csvContent) => {
  const lines = csvContent.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
  const result = [];
  let totalCount = 0;

  for (const line of lines) {
    const lowerLine = line.toLowerCase();
    if (lowerLine.startsWith("username") || lowerLine.startsWith("user name")) {
      continue;
    }

    totalCount++;

    const parts = line.includes(",")
      ? line.split(",").map(part => part.trim())
      : line.split(/\s+/);
    const username = parts[0] || "";
    const password = parts[1] || "";
    const description = line.includes(",")
      ? (parts[2] || "")
      : parts.slice(2).join(" ");

    if (!username || !password) continue;

    result.push({
      username,
      password,
      description,
    });
  }

  return {
    validItems: result,
    totalCount,
  };
};

const ImportView = ({
  viewStateEnum,
  getViewTitle,
  form,
  handleImportView,
}) => {
  const theme = useTheme();
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const closeView = () => {
    handleImportView.closeView();
  };

  const openFileBrowser = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith(".csv")) {
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    e.target.value = "";
  };

  const handleSave = () => {
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const { validItems, totalCount } = parseUserAccountCsv(reader.result || "");
      handleImportView.updateUserAccountItemsList(validItems, totalCount);
      closeView();
    };
    reader.readAsText(file);
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
            paddingLeft: "20px",
          }}
        >
          {getViewTitle(viewStateEnum.IMPORT)}
        </Typography>
        <Button
          onClick={closeView}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <Box
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            alignItems: "center",
            width: "80%",
            gap: "20px",
            marginTop: "40px",
          }}
        >
          <Box sx={form.getLabelStyle()}>Choose File:</Box>
          <Box style={{ maxWidth: "100%" }}>
            <TextField
              variant="outlined"
              size="small"
              value={file ? file.name : ""}
              placeholder="Select a file"
              disabled
              sx={{ ...form.getTextfieldStyle(true), width: "100%" }}
            />
            <input
              type="file"
              ref={fileInputRef}
              accept=".csv,text/csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>
          <OutlineRectangleButton
            startIcon={<AddIcon />}
            height="32px"
            onClick={openFileBrowser}
          >
            Browse
          </OutlineRectangleButton>
        </Box>
      </Box>

      <Box sx={{ margin: "24px 0 16px auto", display: "flex", justifyContent: "center" }}>
        <OutlineButton onClick={closeView} sx={{ marginRight: "20px" }} width="100px" height="32px">
          Cancel
        </OutlineButton>
        <SolidButton onClick={handleSave} width="100px" height="32px" disabled={!file}>
          Upload
        </SolidButton>
      </Box>
    </>
  );
};

export default React.memo(ImportView);
